import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { buttonStyles } from '../../atoms/button/utils/button-styles';
export const NotFound = ({ title, body, renderCta }) => {
    const animationContainer = React.useRef(null);
    // we use a mutable ref to avoid triggering re-renders
    const relativeXPosition = React.useRef(0);
    // Read once: a visitor who prefers reduced motion never gets the pointer/orientation
    // listeners or the RAF scrub loop below — the illustration loads but stays on one frame.
    // Guarded for SSR (the Astro 404 page and Gatsby's 404 templates server-render this
    // component, where `window` is undefined); the value only ever reaches effects below, not
    // the markup, so there's no client/server hydration mismatch to worry about.
    const prefersReducedMotion = typeof window !== 'undefined' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // get the current pointer X position as a number in [0, 1] range
    const getRelativePointerXPosition = (clientX) => 1 - Math.min(clientX, window.innerWidth) / window.innerWidth;
    // Track mouse X position and save it
    React.useEffect(() => {
        if (prefersReducedMotion) {
            return;
        }
        const callback = (event) => {
            relativeXPosition.current = getRelativePointerXPosition(event.clientX);
        };
        document.addEventListener('mousemove', callback, false);
        return () => document.removeEventListener('mousemove', callback);
    }, [prefersReducedMotion]);
    // Track touch X position (only on the image) and save it
    React.useEffect(() => {
        if (prefersReducedMotion) {
            return;
        }
        const callback = (event) => {
            if (event.touches.length === 1) {
                relativeXPosition.current = getRelativePointerXPosition(event.touches[0].clientX);
            }
        };
        const element = animationContainer.current;
        if (!element) {
            return;
        }
        element.addEventListener('touchmove', callback, false);
        return () => element.removeEventListener('touchmove', callback);
    }, [prefersReducedMotion]);
    const initialAngle = React.useRef(null);
    // Track device orientation and save it
    React.useEffect(() => {
        if (prefersReducedMotion) {
            return;
        }
        function handleOrientation(event) {
            const tiltAngle = event.alpha ?? 0;
            if (initialAngle.current === null) {
                initialAngle.current = tiltAngle;
            }
            const MAX_TILT_LEFT = 45;
            const MAX_TILT_RIGHT = -45;
            const clampedTilt = Math.min(Math.max(tiltAngle - initialAngle.current, MAX_TILT_RIGHT), MAX_TILT_LEFT);
            relativeXPosition.current =
                (clampedTilt - MAX_TILT_RIGHT) / (MAX_TILT_LEFT - MAX_TILT_RIGHT);
        }
        window.addEventListener('deviceorientation', handleOrientation, true);
        return () => window.removeEventListener('deviceorientation', handleOrientation, true);
    }, [prefersReducedMotion]);
    // Initialize the Lottie animation in a paused state, and save the reference to the animation
    const lottieAnimation = React.useRef(null);
    React.useEffect(() => {
        const container = animationContainer.current;
        if (!container) {
            return;
        }
        let animation;
        let cancelled = false;
        void (async () => {
            try {
                const [{ default: lottie }, { default: notFoundAnimation }] = await Promise.all([
                    import('lottie-web'),
                    import('./not-found-animation.json'),
                ]);
                if (cancelled) {
                    return;
                }
                animation = lottie.loadAnimation({
                    container,
                    renderer: 'svg',
                    loop: false,
                    autoplay: false,
                    animationData: notFoundAnimation,
                    rendererSettings: { preserveAspectRatio: 'xMidYMin slice' },
                });
                lottieAnimation.current = animation;
                // Reduced motion: rest on the centered frame — the frame the RAF loop below would
                // land on for a pointer at the horizontal middle of the screen (half of the
                // half-length range it scrubs across) — rather than one extreme of that range.
                if (prefersReducedMotion) {
                    const centerFrame = Math.floor((animation.totalFrames ?? 0) / 4);
                    animation.goToAndStop(centerFrame, true);
                }
            }
            catch (error) {
                if (cancelled) {
                    return;
                }
                // The animation is decorative: the 404 copy and CTA render without it, so a failed
                // chunk load degrades to an empty container rather than breaking the page.
                console.error(new Error('NotFound: failed to load the 404 Lottie animation', { cause: error }));
            }
        })();
        return () => {
            cancelled = true;
            animation?.destroy();
            lottieAnimation.current = null;
        };
    }, [prefersReducedMotion]);
    // Run requestAnimationFrame loop, on every RAF frame instruct lottie to animate to the
    // animation frame that corresponds to the current X position of the mouse. Skipped entirely
    // under reduced motion: the load effect above parks the animation on a fixed frame instead.
    const rafId = React.useRef(0);
    React.useEffect(() => {
        if (prefersReducedMotion) {
            return;
        }
        const runFrame = () => {
            const animation = lottieAnimation.current;
            // The animation is loaded asynchronously, so keep scheduling frames until the ref is
            // populated — bailing out here would stop the loop permanently before it ever starts.
            if (animation) {
                // currently the animation is back and forth, so we take half the length as max
                const totalFrames = (animation.totalFrames ?? 0) / 2;
                const videoFrame = Math.floor(totalFrames * relativeXPosition.current);
                animation.goToAndStop(videoFrame, true);
            }
            // set the raf callback for the next frame
            rafId.current = requestAnimationFrame(runFrame);
        };
        // kick start the raf loop
        rafId.current = requestAnimationFrame(runFrame);
        return () => cancelAnimationFrame(rafId.current);
    }, [prefersReducedMotion]);
    return (_jsx("div", { className: "bg-static", children: _jsxs("div", { className: "mx-auto grid w-full max-w-360 grid-flow-dense px-6 pt-0 pb-10 md:min-h-[50vh] md:max-w-437.5 md:grid-cols-[auto_1fr] md:pr-0 md:pb-10 md:pl-10 lg:h-[75vh] xl:pl-25", children: [_jsx("div", { ref: animationContainer, className: "xs:h-full h-75 mix-blend-difference md:order-2 [&_svg]:overflow-visible" }), _jsxs("div", { className: "grid items-center justify-items-start gap-5 self-center", children: [_jsx("h1", { className: "text-dep-xl font-dep-display text-static font-normal break-normal wrap-anywhere", children: title }), _jsx("p", { className: "text-dep-sm font-dep-body-copy text-static font-normal break-normal wrap-anywhere", children: body }), renderCta({
                            className: buttonStyles({ variant: 'pink', shape: 'round' }),
                            tabIndex: 0,
                        })] })] }) }));
};
